<template>
  <div class="mt-6 rounded-sm border border-main bg-surface-elevation-1/75 p-4">
    <div class="flex items-center justify-between">
      <h2 class="text-body-m-bold">Filter</h2>
      <button class="btn-link-neutral-m" @click="filtersExpanded = !filtersExpanded">
        {{ filtersExpanded ? 'Collapse' : 'Expand' }}
        <MdiSvg :path="filtersExpanded ? mdiChevronUp : mdiChevronDown" :size="20" />
      </button>
    </div>

    <template v-if="filtersExpanded">
      <div class="mt-4 flex items-start gap-2">
        <ComboSearch
          :model-value="selectedFilters.companyNames"
          class="shrink-0 grow basis-0 bg-surface"
          :label="$t('VisitorProfiles.filterPanel.searchCompany')"
          :placeholder="$t('ProspectsCatalog.companyNameSearch')"
          @update:model-value="$emit('update:company-names', $event)"
        />
        <ComboSearch
          :model-value="selectedFilters.companyPositions"
          class="shrink-0 grow basis-0 bg-surface"
          :label="$t('VisitorProfiles.filterPanel.searchPosition')"
          :placeholder="$t('ProspectsCatalog.positionSearch')"
          @update:model-value="$emit('update:company-positions', $event)"
        />
      </div>

      <div class="mt-4 flex flex-col justify-stretch gap-4">
        <NestedMultiSelect
          class="bg-surface"
          :model-value="selectedFilters.networkingInterests"
          :label="$t('VisitorProfiles.filterPanel.isLookingFor')"
          :option-groups="networkingOptionsGroups"
          @update:model-value="$emit('update:networking-interests', $event)"
        />

        <div
          v-for="field in userInterestFilterFields"
          :key="field.key"
          :data-testid="`visitor-filter-${field.key}`"
          class="visitor-profiles-dropdown"
        >
          <label class="mb-0.5 block text-body-s-bold">{{ $t(field.labelKey) }}</label>
          <RichSelectField
            :id="`visitor-profiles-user-interest-${field.key}`"
            :model-value="selectedFilters[field.key]"
            class="w-full bg-surface"
            counter
            multiple
            :placeholder="$t('VisitorProfiles.filterPanel.select')"
            searchable
            @update:model-value="field.update($event as string[])"
          >
            <RichSelectOption v-for="option in field.options" :key="option.value" :value="option.value" :label="option.text" />
          </RichSelectField>
        </div>
      </div>

      <div class="mt-4 grid grid-cols-3 gap-4">
        <div
          v-for="field in companyFilterFields"
          :key="field.key"
          :data-testid="`visitor-filter-${field.key}`"
          class="visitor-profiles-dropdown"
        >
          <label class="mb-0.5 block text-body-s-bold">{{ $t(field.labelKey) }}</label>
          <RichSelectField
            :id="`visitor-profiles-company-${field.key}`"
            :model-value="selectedFilters[field.key]"
            class="w-full bg-surface"
            counter
            multiple
            :placeholder="$t('VisitorProfiles.filterPanel.select')"
            searchable
            @update:model-value="field.update($event as string[])"
          >
            <RichSelectOption v-for="option in field.options" :key="option.value" :value="option.value" :label="option.text" />
          </RichSelectField>
        </div>
      </div>
    </template>

    <div v-if="filtersActive" class="mt-4 flex flex-wrap items-center gap-2">
      <div
        v-for="option in selectedFilterFacets"
        :key="option.filter.id"
        class="flex items-center gap-3 rounded-sm border border-primary-subtle-solid bg-primary-subtler px-2 py-1 text-body-s-bold text-primary"
      >
        {{ option.filter.name }}
        <button @click="option.remove()">
          <MdiSvg :path="mdiClose" :size="20" />
        </button>
      </div>

      <button class="flex items-center text-body-s-bold text-subtle" @click="$emit('reset')">
        <MdiSvg :path="mdiClose" :size="20" />
        {{ $t('VisitorProfiles.filterPanel.reset') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiClose, mdiChevronDown, mdiChevronUp } from '@mdi/js'
import { MdiSvg, RichSelectField, RichSelectOption } from '@ramp106/omrjs-core-ui'
import { computed, ref } from 'vue'

import prepareNestedOptionGroups from '@/components/VisitorProfiles/prepareNestedOptionGroups'
import { useCountriesAndNationalitiesData } from '@/composables/useCountriesAndNationalitiesData'
import ComboSearch from '@/ui/ComboSearch.vue'
import NestedMultiSelect from '@/ui/NestedMultiSelect.vue'

import type { FilterType, Facet, NetworkingFacet } from './types'

type SelectedFilterFacet = { filter: Facet; remove: () => void }
type SharedMultiSelectFilterType =
  | 'companyJobLevels'
  | 'companySizes'
  | 'companyFields'
  | 'companyDepartments'
  | 'companyIndustries'
  | 'companyTypes'
  | 'countries'
  | 'userInterests'
type FacetOption = { text: string; value: string }
type SharedMultiSelectField = {
  key: SharedMultiSelectFilterType
  labelKey: string
  options: FacetOption[]
  update: (filters: string[]) => void
}

const props = defineProps<{
  selectedFilters: Record<FilterType, string[]>
  facets?: {
    companyJobLevels: Facet[]
    companySizes: Facet[]
    companyFields: Facet[]
    companyDepartments: Facet[]
    companyIndustries: Facet[]
    companyTypes: Facet[]
    countries: Facet[]
    userInterests: Facet[]
    networkingInterests: NetworkingFacet[]
  }
}>()

const emit = defineEmits<{
  'update:company-job-levels': [filters: string[]]
  'update:company-sizes': [filters: string[]]
  'update:company-names': [filters: string[]]
  'update:company-positions': [filters: string[]]
  'update:company-fields': [filters: string[]]
  'update:company-departments': [filters: string[]]
  'update:company-industries': [filters: string[]]
  'update:company-types': [filters: string[]]
  'update:countries': [filters: string[]]
  'update:user-interests': [filters: string[]]
  'update:networking-interests': [filters: string[]]
  reset: []
}>()

const filtersExpanded = ref(false)

const { countries } = useCountriesAndNationalitiesData()

function createFacetOptions(facets?: Facet[]): FacetOption[] {
  return facets?.map((option) => ({ text: option.name, value: option.id })) ?? []
}

const countryOptions = computed(() => {
  if (!props.facets?.countries) return []
  return props.facets.countries
    .filter((facet) => facet.id)
    .map((facet) => {
      const country = countries.value.find((c) => c.code === facet.id)
      return { text: country?.translation ?? facet.name, value: facet.id }
    })
})

const userInterestFilterFields = computed<SharedMultiSelectField[]>(() => {
  return [
    {
      key: 'userInterests',
      labelKey: 'VisitorProfiles.filterPanel.interests',
      options: createFacetOptions(props.facets?.userInterests),
      update: (filters) => emit('update:user-interests', filters),
    },
  ]
})

const companyFilterFields = computed<SharedMultiSelectField[]>(() => {
  return [
    {
      key: 'companyJobLevels',
      labelKey: 'VisitorProfiles.filterPanel.jobLevel',
      options: createFacetOptions(props.facets?.companyJobLevels),
      update: (filters) => emit('update:company-job-levels', filters),
    },
    {
      key: 'companySizes',
      labelKey: 'VisitorProfiles.filterPanel.companySize',
      options: createFacetOptions(props.facets?.companySizes),
      update: (filters) => emit('update:company-sizes', filters),
    },
    {
      key: 'companyFields',
      labelKey: 'VisitorProfiles.filterPanel.companyField',
      options: createFacetOptions(props.facets?.companyFields),
      update: (filters) => emit('update:company-fields', filters),
    },
    {
      key: 'companyDepartments',
      labelKey: 'VisitorProfiles.filterPanel.companyDepartment',
      options: createFacetOptions(props.facets?.companyDepartments),
      update: (filters) => emit('update:company-departments', filters),
    },
    {
      key: 'companyIndustries',
      labelKey: 'VisitorProfiles.filterPanel.companyIndustry',
      options: createFacetOptions(props.facets?.companyIndustries),
      update: (filters) => emit('update:company-industries', filters),
    },
    {
      key: 'companyTypes',
      labelKey: 'VisitorProfiles.filterPanel.companyType',
      options: createFacetOptions(props.facets?.companyTypes),
      update: (filters) => emit('update:company-types', filters),
    },
    {
      key: 'countries',
      labelKey: 'VisitorProfiles.filterPanel.companyCountry',
      options: countryOptions.value,
      update: (filters) => emit('update:countries', filters),
    },
  ]
})

const networkingOptionsGroups = computed(() => {
  return prepareNestedOptionGroups(props.facets?.networkingInterests ?? [])
})

function createSelectedFilters(selectedFilters: string[], facets: Facet[], emitFunction: (filters: string[]) => void) {
  return selectedFilters
    .map((option) => facets.find((o) => o.id === option))
    .filter((el) => el !== undefined)
    .map((filter) => ({
      filter,
      remove: () => emitFunction(selectedFilters.filter((v) => v !== filter.id)),
    }))
}

const filtersActive = computed(() => {
  return Object.values(props.selectedFilters).some((filters) => filters.length > 0)
})

const selectedFilterFacets = computed(() => {
  if (!props.facets) return []

  const jobLevelFilters = createSelectedFilters(props.selectedFilters.companyJobLevels, props.facets.companyJobLevels, (filters) =>
    emit('update:company-job-levels', filters),
  )

  const companySizeFilters = createSelectedFilters(props.selectedFilters.companySizes, props.facets.companySizes, (filters) =>
    emit('update:company-sizes', filters),
  )

  const companyFieldFilters = createSelectedFilters(props.selectedFilters.companyFields, props.facets.companyFields, (filters) =>
    emit('update:company-fields', filters),
  )

  const companyDepartmentsFilters = createSelectedFilters(
    props.selectedFilters.companyDepartments,
    props.facets.companyDepartments,
    (filters) => emit('update:company-departments', filters),
  )

  const companyIndustriesFilters = createSelectedFilters(
    props.selectedFilters.companyIndustries,
    props.facets.companyIndustries,
    (filters) => emit('update:company-industries', filters),
  )

  const companyTypesFilters = createSelectedFilters(props.selectedFilters.companyTypes, props.facets.companyTypes, (filters) =>
    emit('update:company-types', filters),
  )

  const countriesFilters = props.selectedFilters.countries
    .map((code) => {
      const facet = props.facets!.countries.find((f) => f.id === code)
      if (!facet) return undefined
      const country = countries.value.find((c) => c.code === code)
      return {
        filter: { id: facet.id, name: country?.translation ?? facet.name },
        remove: () =>
          emit(
            'update:countries',
            props.selectedFilters.countries.filter((v) => v !== code),
          ),
      }
    })
    .filter((el) => el !== undefined)

  const userInterestsFilters = createSelectedFilters(props.selectedFilters.userInterests, props.facets.userInterests, (filters) =>
    emit('update:user-interests', filters),
  )

  const networkingInterestsFilters = createSelectedFilters(
    props.selectedFilters.networkingInterests,
    props.facets.networkingInterests,
    (filters) => emit('update:networking-interests', filters),
  )

  const selectedFacets: SelectedFilterFacet[] = [
    ...jobLevelFilters,
    ...companySizeFilters,
    ...companyFieldFilters,
    ...companyDepartmentsFilters,
    ...companyIndustriesFilters,
    ...companyTypesFilters,
    ...countriesFilters,
    ...userInterestsFilters,
    ...networkingInterestsFilters,
  ].sort((a, b) => a.filter.name.localeCompare(b.filter.name))

  return selectedFacets
})
</script>
